import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Compass, Layers, Sparkles, ArrowRight, CircleCheckBig, Circle, Pencil, CalendarDays, LineChart, Users, X, ChevronLeft, ChevronRight, Rocket } from "lucide-react";
import { api } from "../lib/api";
import { useAuth } from "../lib/auth";

const TUTORIAL_STEPS = [
  { key: "posicionamento", label: "1. Definir posicionamento", desc: "Wizard de 14 etapas — do diagnóstico à frase final.", to: "/posicionamento", icon: Compass },
  { key: "workspace", label: "2. Estruturar workspace", desc: "7 camadas: planejamento, produtos, agenda, aprovação e mais.", to: "/workspace", icon: Layers },
  { key: "conteudo", label: "3. Roteiro persuasivo", desc: "6 passos para transformar tese em post que converte.", to: "/conteudo", icon: Pencil },
  { key: "configuracoes", label: "4. Ligar sua IA", desc: "Cole sua chave (Claude, Gemini ou OpenAI) para ativar os assistentes.", to: "/configuracoes", icon: Sparkles },
];

const TOUR_SLIDES = [
  {
    icon: Rocket,
    title: "Bem-vindo ao Social Booster",
    body: "Este é o control-room de estratégia + conteúdo da sua marca. Em 6 telas você entende o fluxo completo. Você pode fechar quando quiser e voltar clicando em 'Rever tour'.",
  },
  {
    icon: Compass,
    title: "Posicionamento",
    body: "O coração do sistema. Um wizard de 14 etapas leva você do diagnóstico à frase de posicionamento final, com mapa perceptual interativo, personalidade e pilares. Todo o resto do app usa esses dados como contexto.",
    to: "/posicionamento",
    cta: "Abrir Posicionamento",
  },
  {
    icon: Layers,
    title: "Workspace",
    body: "7 camadas operacionais: planejamento estratégico, produtos, canais, mix editorial, agenda, aprovação e swipe file. Auto-save por debounce — nada se perde.",
    to: "/workspace",
    cta: "Abrir Workspace",
  },
  {
    icon: CalendarDays,
    title: "Campanhas & Conteúdo",
    body: "Trabalhe por campanha do mês: mote + semanas (pilar+direção) + posts multidimensionais (data, formato, canal, quem faz, etapa do funil, direcionamento). Um clique gera o roteiro persuasivo no módulo Conteúdo já pré-preenchido.",
    to: "/calendario",
    cta: "Abrir Campanhas",
  },
  {
    icon: LineChart,
    title: "Funil & Performance",
    body: "Você registra os números manualmente e a IA lê à luz do objetivo da marca. Retorna gargalo, leitura vs objetivo e 3 recomendações acionáveis. É consultoria, não dashboard.",
    to: "/funil",
    cta: "Abrir Funil",
  },
  {
    icon: Sparkles,
    title: "Ligar IA + Convidar time",
    body: "Em Configurações, cole sua chave Claude/Gemini/OpenAI (fica criptografada AES) para ativar todos os assistentes. Ative 2FA, convide editores/aprovadores para a org e, se usar o LABrand, vincule a marca em 1 clique.",
    to: "/configuracoes",
    cta: "Abrir Configurações",
  },
];

function ProgressiveTour({ onClose, onNavigate }) {
  const [idx, setIdx] = useState(0);
  const slide = TOUR_SLIDES[idx];
  const Icon = slide.icon;
  const isLast = idx === TOUR_SLIDES.length - 1;

  useEffect(() => {
    const onKey = (e) => {
      if (e.key === "Escape") onClose();
      else if (e.key === "ArrowRight" && !isLast) setIdx((i) => Math.min(TOUR_SLIDES.length - 1, i + 1));
      else if (e.key === "ArrowLeft") setIdx((i) => Math.max(0, i - 1));
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose, isLast]);

  const goTo = () => {
    if (slide.to) {
      onClose();
      onNavigate(slide.to);
    }
  };

  return (
    <div className="tour-overlay" data-testid="tour-overlay" role="dialog" aria-modal="true" aria-labelledby="tour-title" onClick={onClose}>
      <div className="tour-tip" style={{ top: "50%", left: "50%", transform: "translate(-50%, -50%)", maxWidth: 520, width: "calc(100% - 32px)" }} onClick={(e) => e.stopPropagation()}>
        <button onClick={onClose} className="absolute right-3 top-3 text-muted hover:text-white" data-testid="tour-skip" aria-label="Fechar tour"><X size={16} /></button>
        <div className="flex items-center gap-3 mb-3">
          <div className="w-11 h-11 rounded-xl grad-bg flex items-center justify-center flex-shrink-0">
            <Icon size={20} className="text-white" />
          </div>
          <div>
            <div className="font-mono text-[10px] uppercase tracking-widest grad-text font-bold">// tour · passo {idx + 1} de {TOUR_SLIDES.length}</div>
            <h3 id="tour-title" className="font-extrabold text-lg leading-tight">{slide.title}</h3>
          </div>
        </div>
        <p className="text-sm text-muted leading-relaxed mb-4">{slide.body}</p>

        {/* Progress dots */}
        <div className="flex items-center gap-1.5 mb-4" data-testid="tour-dots">
          {TOUR_SLIDES.map((_, i) => (
            <button
              key={i}
              onClick={() => setIdx(i)}
              className={`h-1.5 rounded-full transition-all ${i === idx ? "w-6 grad-bg" : "w-1.5"}`}
              style={{ background: i === idx ? undefined : "var(--border)" }}
              aria-label={`Ir para o passo ${i + 1}`}
              data-testid={`tour-dot-${i}`}
            />
          ))}
        </div>

        <div className="flex items-center justify-between gap-2">
          <button
            onClick={() => setIdx((i) => Math.max(0, i - 1))}
            disabled={idx === 0}
            className="btn-outline text-xs flex items-center gap-1 disabled:opacity-40 disabled:cursor-not-allowed"
            data-testid="tour-prev"
          >
            <ChevronLeft size={12} /> Voltar
          </button>
          <div className="flex items-center gap-2">
            {slide.to && slide.cta && (
              <button onClick={goTo} className="btn-outline text-xs" data-testid="tour-goto">{slide.cta}</button>
            )}
            {isLast ? (
              <button onClick={onClose} className="btn-grad text-xs flex items-center gap-1" data-testid="tour-finish">
                Concluir <CircleCheckBig size={12} />
              </button>
            ) : (
              <button onClick={() => setIdx((i) => Math.min(TOUR_SLIDES.length - 1, i + 1))} className="btn-grad text-xs flex items-center gap-1" data-testid="tour-next">
                Próximo <ChevronRight size={12} />
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

export default function Dashboard() {
  const { user, activeBrand } = useAuth();
  const navigate = useNavigate();
  const [pos, setPos] = useState(null);
  const [ws, setWs] = useState(null);
  const [llmCfg, setLlmCfg] = useState({ has_key: false });
  const [content, setContent] = useState({ roteiros: [], big_ideas: [] });
  const [showTour, setShowTour] = useState(false);

  // Per-user tour key (so different accounts on the same browser each see the tour once)
  const tourKey = user?.user_id ? `sb_tour_seen_${user.user_id}` : "sb_tour_seen";

  useEffect(() => {
    if (!activeBrand) return;
    (async () => {
      try {
        const [p, w, l, c] = await Promise.all([
          api.get(`/positioning/${activeBrand.brand_id}`),
          api.get(`/workspace/${activeBrand.brand_id}`),
          api.get(`/llm/config`),
          api.get(`/content/${activeBrand.brand_id}`),
        ]);
        setPos(p.data);
        setWs(w.data);
        setLlmCfg(l.data);
        setContent(c.data);
      } catch { /* noop */ }
    })();
    // Migração one-shot: se a chave legacy existir + o user ainda não tem chave própria,
    // migra pra chave user-scoped e apaga a legacy. Depois disso cada user tem sua vida.
    if (user?.user_id) {
      const legacy = localStorage.getItem("sb_tour_seen");
      const seen = localStorage.getItem(tourKey);
      if (legacy && !seen) {
        localStorage.setItem(tourKey, "1");
        localStorage.removeItem("sb_tour_seen");
      } else if (!seen) {
        setShowTour(true);
      }
    }
  }, [activeBrand, tourKey, user?.user_id]);

  const closeTour = () => {
    localStorage.setItem(tourKey, "1");
    setShowTour(false);
  };

  const reopenTour = () => {
    localStorage.removeItem(tourKey);
    localStorage.removeItem("sb_tour_seen");
    setShowTour(true);
  };

  const stepStatus = {
    posicionamento: pos?.progress?.completed > 0 ? "started" : "pending",
    workspace: ws?.progress?.completed > 0 ? "started" : "pending",
    conteudo: content?.roteiros?.length > 0 ? "started" : "pending",
    configuracoes: llmCfg?.has_key ? "done" : "pending",
  };

  return (
    <div className="p-6 lg:p-10" data-testid="dashboard-page">
      {showTour && <ProgressiveTour onClose={closeTour} onNavigate={(to) => navigate(to)} />}

      {/* Banner */}
      <div className="card p-6 mb-6 relative overflow-hidden">
        <div className="absolute inset-0 opacity-20" style={{ background: "radial-gradient(circle at 90% 0%, rgba(192,91,255,0.5), transparent 45%)" }} />
        <div className="relative flex items-start justify-between gap-4">
          <div>
            <div className="font-mono text-[10px] uppercase tracking-widest grad-text font-bold mb-1">// {new Date().toLocaleDateString("pt-BR", { weekday: "long" })}</div>
            <h1 className="text-3xl font-extrabold">Olá, {user?.name?.split(" ")[0]}.</h1>
            <p className="text-muted mt-1">Continue evoluindo <span className="font-semibold text-[var(--txt)]">{activeBrand?.name}</span> no sistema.</p>
          </div>
          <div className="flex items-center gap-2">
            <span className={`chip ${activeBrand?.source === "labrand" ? "grad" : ""}`}>
              {activeBrand?.source === "labrand" ? "via LABrand" : "cadastro manual"}
            </span>
            <button onClick={reopenTour} className="btn-outline text-xs" data-testid="reopen-tour">Rever tour</button>
          </div>
        </div>
      </div>

      {/* Tutorial */}
      <div className="mb-6">
        <div className="flex items-baseline justify-between mb-3">
          <h2 className="text-lg font-bold">Tutorial passo a passo</h2>
          <span className="text-xs text-muted font-mono">4 etapas</span>
        </div>
        <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-4">
          {TUTORIAL_STEPS.map((s) => {
            const Icon = s.icon;
            const status = stepStatus[s.key];
            return (
              <Link key={s.key} to={s.to} className="card hover-lift p-5 space-y-3 relative" data-testid={`tutorial-${s.key}`}>
                <div className="flex items-start justify-between">
                  <div className="w-11 h-11 rounded-xl grad-bg flex items-center justify-center">
                    <Icon size={20} className="text-white" />
                  </div>
                  {status === "done" ? (
                    <CircleCheckBig size={20} className="text-[var(--success)]" />
                  ) : status === "started" ? (
                    <span className="chip">em progresso</span>
                  ) : (
                    <Circle size={20} className="text-muted" />
                  )}
                </div>
                <div>
                  <div className="font-bold">{s.label}</div>
                  <p className="text-sm text-muted mt-1">{s.desc}</p>
                </div>
                <div className="flex items-center gap-2 text-sm grad-text font-semibold">
                  Abrir <ArrowRight size={14} />
                </div>
              </Link>
            );
          })}
        </div>
      </div>

      {/* Quick stats */}
      <div className="grid md:grid-cols-2 gap-4">
        <div className="card p-5">
          <div className="flex items-center justify-between mb-3">
            <div>
              <div className="font-mono text-[10px] uppercase tracking-widest text-muted">// posicionamento</div>
              <div className="text-2xl font-extrabold mt-1">{pos?.progress?.percent ?? 0}<span className="text-base text-muted">%</span></div>
            </div>
            <Link to="/posicionamento" className="btn-outline text-xs">Continuar</Link>
          </div>
          <div className="progress"><div style={{ width: `${pos?.progress?.percent ?? 0}%` }} /></div>
          <div className="text-xs text-muted mt-2">{pos?.progress?.completed ?? 0} de {pos?.progress?.total ?? 14} etapas concluídas</div>
        </div>
        <div className="card p-5">
          <div className="flex items-center justify-between mb-3">
            <div>
              <div className="font-mono text-[10px] uppercase tracking-widest text-muted">// workspace</div>
              <div className="text-2xl font-extrabold mt-1">{ws?.progress?.percent ?? 0}<span className="text-base text-muted">%</span></div>
            </div>
            <Link to="/workspace" className="btn-outline text-xs">Continuar</Link>
          </div>
          <div className="progress"><div style={{ width: `${ws?.progress?.percent ?? 0}%` }} /></div>
          <div className="text-xs text-muted mt-2">{ws?.progress?.completed ?? 0} de {ws?.progress?.total ?? 7} camadas preenchidas</div>
        </div>
      </div>
    </div>
  );
}
